import { GoogleConnector } from '@logto/connector-kit';

/**
 * Apple and Google fix the fill, the label colour and the mark of their sign-in buttons; a
 * restyled one is a terms violation, so these two pin their colours.
 */
export type SocialBrand = 'apple' | 'google';

/** No `AppleConnector` constant exists in connector-kit; the connector's own metadata pins this. */
const appleTarget = 'apple';

/**
 * Put Sign in with Apple first on Apple platforms.
 *
 * App Store Review 4.8 requires Sign in with Apple to be offered with equivalent
 * prominence wherever other third-party sign-in is offered, and the native app already
 * leads with it on iOS. The hosted page is reached FROM that app, so a provider order
 * that flips mid-flow is both inconsistent and the thing 4.8 is about.
 *
 * Only Apple platforms are reordered; everywhere else the tenant's configured order is
 * left exactly as it is, because this is Apple's rule and not a general ranking.
 * Stable: the relative order of every other provider is preserved.
 */
export const withApplePriority = <T extends { target: string }>(
  connectors: readonly T[],
  isApplePlatform: boolean
): readonly T[] => {
  if (!isApplePlatform) {
    return connectors;
  }

  const apple = connectors.filter(({ target }) => target === appleTarget);
  return apple.length > 0
    ? [...apple, ...connectors.filter(({ target }) => target !== appleTarget)]
    : connectors;
};

/**
 * Resolve from a *social* connector target only. SSO connectors pass a tenant-authored
 * display name as their target, so a workspace called "Google" must never pick up Google's
 * button — which is why this is called by the social list rather than inside the buttons.
 */
export const getSocialBrand = (target: string): SocialBrand | undefined => {
  if (target === appleTarget) {
    return 'apple';
  }

  if (target === GoogleConnector.target) {
    return 'google';
  }

  return undefined;
};

/**
 * Every provider shares the outline option button (the outlined styles both Apple and Google
 * permit). Apple ships one monochrome mark and the tenant may store either the black or the
 * white variant, so it is collapsed to the ink of the current theme; Google's four-colour G
 * must never be recoloured.
 */
export const socialBrandLogoClass: Record<SocialBrand, string> = {
  apple: '[filter:brightness(0)] dark:[filter:brightness(0)_invert(1)]',
  google: '',
};
